import { Boundaries } from "@planzone/core";
import { projectBudgetSynthesis } from "@planzone/dto";
import { events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRequest(
  state: State,
  { project, date }: Boundaries["retrieveProjectBudgetSynthesis"]["request"],
): State {
  const index = state.findIndex(
    (req) => req.project.uuid === project.uuid && req.date === date,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          synthesis: projectBudgetSynthesis,
          request: setRequestStateToPending(),
          date,
        },
      ];
}

function handleSuccess(
  state: State,
  {
    project,
    budgetSynthesis,
    date,
  }: Boundaries["retrieveProjectBudgetSynthesis"]["success"],
): State {
  const index = state.findIndex(
    (req) => req.project.uuid === project.uuid && req.date === date,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          synthesis: budgetSynthesis,
          request: setRequestStateToSuccess(),
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          synthesis: budgetSynthesis,
          request: setRequestStateToSuccess(),
          date,
        },
      ];
}

function handleFailure(
  state: State,
  { project, date }: Boundaries["retrieveProjectBudgetSynthesis"]["failure"],
): State {
  const index = state.findIndex(
    (req) => req.project.uuid === project.uuid && req.date === date,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          synthesis: projectBudgetSynthesis,
          request: setRequestStateToFailure(),
          date,
        },
      ];
}

function handleEditProjectAllocatedResourcesBudget(
  state: State,
  {
    allocatedResourcesBudget,
    project,
  }: Boundaries["editProjectAllocatedResourcesBudget"]["success"],
): State {
  const index = state.findIndex((req) => req.project.uuid === project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          request: {
            isPending: false,
            isSuccessful: false,
            isFailure: false,
          },
          synthesis: {
            ...state[index].synthesis,
            allocatedResourcesBudget: allocatedResourcesBudget.toString(),
          },
        },
        ...state.slice(index + 1),
      ]
    : state;
}

function handleEditProjectAllocatedExpensesBudget(
  state: State,
  {
    allocatedExpensesBudget,
    project,
  }: Boundaries["editProjectAllocatedExpensesBudget"]["success"],
): State {
  const index = state.findIndex((req) => req.project.uuid === project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          request: {
            isPending: false,
            isSuccessful: false,
            isFailure: false,
          },
          synthesis: {
            ...state[index].synthesis,
            allocatedExpensesBudget: allocatedExpensesBudget.toString(),
          },
        },
        ...state.slice(index + 1),
      ]
    : state;
}

function handleEditExpenses(state: State): State {
  return state.map((req) => ({
    ...req,
    request: {
      isPending: false,
      isSuccessful: false,
      isFailure: false,
    },
  }));
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrieveProjectBudgetSynthesisRequested]: handleRequest,
    [events.retrieveProjectBudgetSynthesisSucceeded]: handleSuccess,
    [events.retrieveProjectBudgetSynthesisFailed]: handleFailure,
    [events.editProjectAllocatedResourcesBudgetSucceeded]:
      handleEditProjectAllocatedResourcesBudget,
    [events.editProjectAllocatedExpensesBudgetSucceeded]:
      handleEditProjectAllocatedExpensesBudget,
    [events.editProjectExpenseSucceeded]: handleEditExpenses,
    [events.removeProjectExpenseSucceeded]: handleEditExpenses,
    [events.createProjectExpenseSucceeded]: handleEditExpenses,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
